import { administratorPermissionsAPI } from './administrator-permissions.api'
import type {
  AvailablePermissionsResponse,
  AdministratorPermissionsResponse,
  CreateAdministratorPermissionsRequest,
  UpdateAdministratorPermissionsRequest,
  GrantPermissionRequest,
  SetSuperAdminRequest
} from './administrator-permissions.types'

/**
 * Administrator Permissions service composable -- pure async business logic.
 *
 * NO reactive state (loading, error refs) -- the store handles that.
 * This layer wraps the API and handles data unwrapping.
 *
 * Architecture: View -> Store -> administrator-permissions.composable.ts -> administrator-permissions.api.ts
 */
export function useAdministratorPermissionsService() {
  /**
   * Get all available admin permissions with metadata.
   */
  const getAvailablePermissions = async (): Promise<AvailablePermissionsResponse> => {
    const response = await administratorPermissionsAPI.getAvailablePermissions()
    return response.data
  }

  /**
   * Get permissions for a specific administrator.
   */
  const getPermissions = async (administratorId: number): Promise<AdministratorPermissionsResponse> => {
    const response = await administratorPermissionsAPI.getPermissions(administratorId)
    return response.data
  }

  /**
   * Create permissions for a new administrator.
   */
  const createPermissions = async (
    administratorId: number,
    data: CreateAdministratorPermissionsRequest
  ): Promise<AdministratorPermissionsResponse> => {
    const response = await administratorPermissionsAPI.createPermissions(administratorId, data)
    return response.data
  }

  /**
   * Update permissions for an existing administrator.
   */
  const updatePermissions = async (
    administratorId: number,
    data: UpdateAdministratorPermissionsRequest
  ): Promise<AdministratorPermissionsResponse> => {
    const response = await administratorPermissionsAPI.updatePermissions(administratorId, data)
    return response.data
  }

  /**
   * Grant or revoke a single permission for an administrator.
   */
  const grantOrRevokePermission = async (
    administratorId: number,
    data: GrantPermissionRequest
  ): Promise<void> => {
    await administratorPermissionsAPI.grantOrRevokePermission(administratorId, data)
  }

  /**
   * Set or remove super admin status for an administrator.
   */
  const setSuperAdmin = async (
    administratorId: number,
    data: SetSuperAdminRequest
  ): Promise<void> => {
    await administratorPermissionsAPI.setSuperAdmin(administratorId, data)
  }

  /**
   * Delete permissions for an administrator (reverts to no permissions).
   */
  const deletePermissions = async (administratorId: number): Promise<void> => {
    await administratorPermissionsAPI.deletePermissions(administratorId)
  }

  /**
   * Get permissions for the current logged-in administrator.
   */
  const getMyPermissions = async (): Promise<AdministratorPermissionsResponse> => {
    const response = await administratorPermissionsAPI.getMyPermissions()
    return response.data
  }

  return {
    getAvailablePermissions,
    getPermissions,
    createPermissions,
    updatePermissions,
    grantOrRevokePermission,
    setSuperAdmin,
    deletePermissions,
    getMyPermissions
  }
}
